import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import AdmissionForm from "./pages/AdmissionForm";
import BaseManagement from "./pages/BaseManagement";
import AnimalSearch from "./pages/AnimalSearch";
import AnimalRecord from "./pages/AnimalRecord";
import OrganizationsPage from "./pages/OrganizationsPage";
import BasesMapPage from "./pages/BasesMapPage";
import MedicationDashboard from "./pages/MedicationDashboard";
import FeedingDashboard from "./pages/FeedingDashboard";
import CleaningDashboard from "./pages/CleaningDashboard";
import SequenceConfigPage from "./pages/SequenceConfigPage";
import SpeciesCatalogPage from "./pages/SpeciesCatalogPage";
import ReportsPage from "./pages/ReportsPage";
import LabRequestsPage from "./pages/LabRequestsPage";
import QuickAnimalActionsPage from "./pages/QuickAnimalActionsPage";
import QRScannerPage from "./pages/QRScannerPage";
import UsersPage from "./pages/UsersPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import SupportTicketsPage from "./pages/SupportTicketsPage";
import AssistantBot from "./components/AssistantBot";
import MaintenanceNotice from "./components/MaintenanceNotice";
import SystemNoticesPage from "./pages/SystemNoticesPage";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { hasAnyPermission } from "./lib/permissions";
export { API } from "./lib/api";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, allowedPermissions }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          <p className="mt-4 text-emerald-700">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    toast.error("Você não tem permissão para acessar esta página");
    return <Navigate to="/" replace />;
  }

  if (allowedPermissions && !hasAnyPermission(user, allowedPermissions)) {
    toast.error("Você não tem permissão para acessar esta página");
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute allowedPermissions={["dashboards.view"]}>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admissions/new"
        element={
          <ProtectedRoute allowedPermissions={["animals.edit_admin_data"]}>
            <AdmissionForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admissions/:id"
        element={
          <ProtectedRoute allowedPermissions={["animals.edit_admin_data"]}>
            <AdmissionForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bases"
        element={
          <ProtectedRoute allowedPermissions={["bases.manage"]}>
            <BaseManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/animals"
        element={
          <ProtectedRoute>
            <AnimalSearch />
          </ProtectedRoute>
        }
      />
      <Route
        path="/animals/:id/record"
        element={
          <ProtectedRoute allowedPermissions={["animals.view"]}>
            <AnimalRecord />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organizations"
        element={
          <ProtectedRoute allowedPermissions={["organizations.manage"]}>
            <OrganizationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bases/map"
        element={
          <ProtectedRoute allowedPermissions={["animals.view"]}>
            <BasesMapPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/medications"
        element={
          <ProtectedRoute allowedPermissions={["medication.dashboard.view"]}>
            <MedicationDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feeding"
        element={
          <ProtectedRoute allowedPermissions={["feeding.dashboard.view"]}>
            <FeedingDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cleaning"
        element={
          <ProtectedRoute allowedPermissions={["cleaning.view"]}>
            <CleaningDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/sequence"
        element={
          <ProtectedRoute allowedPermissions={["sequence.manage"]}>
            <SequenceConfigPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/species"
        element={
          <ProtectedRoute allowedPermissions={["species.manage"]}>
            <SpeciesCatalogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedPermissions={["reports.view"]}>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lab-requests"
        element={
          <ProtectedRoute allowedPermissions={["lab_requests.view"]}>
            <LabRequestsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedPermissions={["users.view"]}>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit"
        element={
          <ProtectedRoute allowedRoles={["programador"]}>
            <AuditLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notices"
        element={
          <ProtectedRoute allowedRoles={["programador"]}>
            <SystemNoticesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute allowedPermissions={["support_tickets.view"]}>
            <SupportTicketsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/qr/animal/:id"
        element={
          <ProtectedRoute allowedPermissions={["animals.view"]}>
            <QuickAnimalActionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/qr-scanner"
        element={
          <ProtectedRoute allowedPermissions={["qr.use"]}>
            <QRScannerPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  const isMicrosoftPopupResponse =
    window.opener &&
    window.location.hash &&
    /[#&](code|id_token|access_token|error|state)=/.test(window.location.hash);

  if (isMicrosoftPopupResponse) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-sm text-slate-600">Concluindo autenticação...</p>
      </div>
    );
  }

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <MaintenanceNotice />
          <AssistantBot />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
